import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VideoOff, Minimize2, Maximize2, Move, PictureInPicture2 } from 'lucide-react';

/**
 * LiveClassFloatingCameraPip
 * Persistent, draggable Picture-in-Picture window for LiveKit classes.
 * Displays remote participant camera as primary feed + optional local self-preview inset.
 */
export default function LiveClassFloatingCameraPip({
  remoteCameraTrack = null,
  localCameraTrack = null,
  remoteParticipantName = 'Participant',
  remoteParticipantRole = 'Student',
  isRemoteVideoMuted = false,
  isLocalVideoMuted = false,
  isDark = true,
  containerRef = null, // Optional boundary element to clamp position inside
  initialPosition = null,
}) {
  const pipRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  // Position state (default: top-right corner)
  const [position, setPosition] = useState(() => {
    if (initialPosition) return initialPosition;
    if (typeof window !== 'undefined') {
      const w = window.innerWidth;
      return {
        x: Math.max(16, w - 240),
        y: 80,
      };
    }
    return { x: 20, y: 80 };
  });

  // Collapsed state: allows 1-tap minimize to a small floating badge
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Self-preview toggle inside PiP (show/hide local inset)
  const [showSelfPreview] = useState(true);

  // Dragging state
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const hasMovedRef = useRef(false);

  // Bounds clamping helper
  const clampPosition = useCallback((x, y) => {
    if (typeof window === 'undefined') return { x, y };

    let minX = 12;
    let minY = 60;
    let maxX = window.innerWidth - (pipRef.current?.offsetWidth || 220) - 12;
    let maxY = window.innerHeight - (pipRef.current?.offsetHeight || 140) - 16;

    if (containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      minX = Math.max(minX, rect.left + 8);
      minY = Math.max(minY, rect.top + 8);
      maxX = Math.min(maxX, rect.right - (pipRef.current?.offsetWidth || 220) - 8);
      maxY = Math.min(maxY, rect.bottom - (pipRef.current?.offsetHeight || 140) - 8);
    }

    return {
      x: Math.min(Math.max(minX, x), Math.max(minX, maxX)),
      y: Math.min(Math.max(minY, y), Math.max(minY, maxY)),
    };
  }, [containerRef]);

  // Handle window resize or orientation change
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [clampPosition]);

  // Attach/detach remote camera track
  useEffect(() => {
    if (remoteVideoRef.current && remoteCameraTrack) {
      remoteCameraTrack.attach(remoteVideoRef.current);
      return () => {
        remoteCameraTrack.detach(remoteVideoRef.current);
      };
    }
  }, [remoteCameraTrack, isCollapsed]);

  // Attach/detach local self-preview track
  useEffect(() => {
    if (localVideoRef.current && localCameraTrack && showSelfPreview && !isCollapsed) {
      localCameraTrack.attach(localVideoRef.current);
      return () => {
        localCameraTrack.detach(localVideoRef.current);
      };
    }
  }, [localCameraTrack, showSelfPreview, isCollapsed]);

  // Mouse Drag Handling (Shields canvas/drawing from event bleed)
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    // Don't drag if clicking buttons
    if (e.target.closest('button')) return;

    e.stopPropagation();
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };

    const handleMouseMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      moveEvent.stopPropagation();
      const dx = moveEvent.clientX - dragStartRef.current.startX;
      const dy = moveEvent.clientY - dragStartRef.current.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        hasMovedRef.current = true;
        setPosition(clampPosition(dragStartRef.current.initialX + dx, dragStartRef.current.initialY + dy));
      }
    };

    const handleMouseUp = (upEvent) => {
      upEvent.stopPropagation();
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Touch Drag Handling (Mobile touch shield)
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    if (e.target.closest('button')) return;

    e.stopPropagation();
    const touch = e.touches[0];
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    dragStartRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      initialX: position.x,
      initialY: position.y,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current || e.touches.length !== 1) return;
    e.stopPropagation();
    e.preventDefault(); // Prevent page scroll / canvas draw
    const touch = e.touches[0];
    const dx = touch.clientX - dragStartRef.current.startX;
    const dy = touch.clientY - dragStartRef.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      hasMovedRef.current = true;
      setPosition(clampPosition(dragStartRef.current.initialX + dx, dragStartRef.current.initialY + dy));
    }
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    isDraggingRef.current = false;
  };

  // Native Browser Picture-in-Picture trigger
  const handleNativePipToggle = async () => {
    if (!remoteVideoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (remoteVideoRef.current.requestPictureInPicture) {
        await remoteVideoRef.current.requestPictureInPicture();
      }
    } catch (err) {
      console.warn('Native Picture-in-Picture failed:', err);
    }
  };

  const initial = (remoteParticipantName || 'P').trim().charAt(0).toUpperCase();
  const canNativePip = typeof document !== 'undefined' && document.pictureInPictureEnabled;

  // -------------------------------------------------------------
  // Render: Collapsed Badge Mode (Minimal floating pill icon)
  // -------------------------------------------------------------
  if (isCollapsed) {
    return (
      <div
        ref={pipRef}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          touchAction: 'none',
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="z-[10001] select-none cursor-grab active:cursor-grabbing flex items-center gap-1.5 p-1.5 pr-2.5 rounded-full shadow-2xl backdrop-blur-xl border border-emerald-500/40 bg-slate-900/90 text-white animate-in zoom-in-95 duration-150"
      >
        <button
          type="button"
          onClick={() => setIsCollapsed(false)}
          className="w-8 h-8 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center shadow transition shrink-0"
          title="Expand Camera View"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        <span className="text-xs font-bold truncate max-w-[100px] leading-tight">
          {remoteParticipantName}
        </span>
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
      </div>
    );
  }

  // -------------------------------------------------------------
  // Render: Expanded Dual-PiP Window
  // -------------------------------------------------------------
  return (
    <div
      ref={pipRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: 'none',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`z-[10001] select-none cursor-grab active:cursor-grabbing flex flex-col rounded-2xl shadow-2xl backdrop-blur-xl border overflow-hidden transition-shadow duration-200 ${
        isDark
          ? 'bg-slate-900/95 border-slate-700/80 shadow-black/70 text-white'
          : 'bg-white/95 border-slate-200/90 shadow-slate-900/30 text-slate-800'
      } w-44 sm:w-56 md:w-64`}
    >
      {/* Header Bar with Drag Handle & Controls */}
      <div
        className={`px-2.5 py-1.5 flex items-center justify-between border-b text-[11px] font-bold ${
          isDark ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-slate-100/70'
        }`}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Move className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="truncate max-w-[100px] sm:max-w-[120px]">
            {remoteParticipantName}
          </span>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {/* Native Browser PiP Button (Desktop only) */}
          {canNativePip && remoteCameraTrack && (
            <button
              type="button"
              onClick={handleNativePipToggle}
              className={`p-1 rounded-md transition ${
                isDark ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-slate-200 text-slate-600'
              }`}
              title="Pop out to OS Floating Window"
            >
              <PictureInPicture2 className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Minimize / Collapse Button */}
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            className={`p-1 rounded-md transition ${
              isDark ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-slate-200 text-slate-600'
            }`}
            title="Minimize to Floating Badge"
          >
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Primary Video Container: Remote Participant Camera */}
      <div className="relative aspect-video w-full bg-black overflow-hidden flex items-center justify-center">
        {/* Remote Camera Video Element */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className={`w-full h-full object-cover ${
            !remoteCameraTrack || isRemoteVideoMuted ? 'hidden' : 'block'
          }`}
        />

        {/* Fallback when Remote Camera is OFF */}
        {(!remoteCameraTrack || isRemoteVideoMuted) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center bg-slate-900/90 text-white">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 mb-1 shadow-inner">
              <span className="text-sm font-bold text-white">{initial}</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
              <VideoOff className="w-3 h-3 text-rose-400" />
              <span>Camera Off</span>
            </div>
          </div>
        )}

        {/* Nested Self-Preview Corner Inset */}
        {showSelfPreview && localCameraTrack && (
          <div
            className={`absolute bottom-1.5 right-1.5 z-10 w-14 h-10 sm:w-16 sm:h-12 rounded-lg overflow-hidden border shadow-lg bg-black ${
              isDark ? 'border-slate-700/80' : 'border-slate-300'
            }`}
          >
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${isLocalVideoMuted ? 'hidden' : 'block'}`}
            />
            {isLocalVideoMuted && (
              <div className="w-full h-full flex items-center justify-center bg-slate-900 text-slate-400">
                <VideoOff className="w-3.5 h-3.5 text-slate-400" />
              </div>
            )}
            <span className="absolute bottom-0.5 left-0.5 px-1 py-0 text-[8px] font-bold bg-slate-950/80 text-white rounded">
              You
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
